/**
 * Single Review & Confirm candidate row: checkbox, name edit, suggestion, category/qty, discard.
 */
import { useEffect, useState } from 'react'
import { Button, Input } from '../../design-system/primitives/index.js'

/**
 * @param {{
 *   item: {
 *     id: string,
 *     name: string,
 *     checked: boolean,
 *     confidence: 'high' | 'low',
 *     suggestion?: string | null,
 *     category?: string | null,
 *     quantity_display?: string | null,
 *   },
 *   editingId: string | null,
 *   onToggle: (id: string) => void,
 *   onStartEdit: (id: string) => void,
 *   onCommitEdit: (id: string, name: string) => void,
 *   onCancelEdit: () => void,
 *   onDiscard: (id: string) => void,
 *   onApplySuggestion: (id: string, suggestion: string) => void,
 * }} props
 */
export default function PantryCandidateRow({
  item,
  editingId,
  onToggle,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onDiscard,
  onApplySuggestion,
}) {
  const [draft, setDraft] = useState(item.name)
  const isEditing = editingId === item.id

  useEffect(() => {
    if (isEditing) setDraft(item.name)
  }, [isEditing, item.name])

  return (
    <div
      className={`pantry-review-row${item.confidence === 'low' && !item.checked ? ' pantry-review-row--needs-look' : ''}`}
    >
      <input
        type="checkbox"
        className="pantry-review-row__check"
        checked={item.checked}
        onChange={() => onToggle(item.id)}
        aria-label={`Include ${item.name}`}
      />

      {isEditing ? (
        <div className="pantry-review-row__edit">
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                const next = draft.trim()
                if (next) onCommitEdit(item.id, next)
              }
              if (e.key === 'Escape') {
                e.preventDefault()
                onCancelEdit()
              }
            }}
            aria-label="Edit item name"
            className="pantry-review-row__edit-input"
          />
          <Button
            type="button"
            variant="primary"
            className="pantry-review-row__edit-save"
            onClick={() => {
              const next = draft.trim()
              if (next) onCommitEdit(item.id, next)
            }}
            disabled={!draft.trim()}
          >
            Save
          </Button>
          <Button type="button" variant="ghost" className="pantry-review-row__edit-cancel" onClick={onCancelEdit}>
            Cancel
          </Button>
        </div>
      ) : (
        <>
          <div className="pantry-review-row__main">
            <div className="pantry-review-row__name-line">
              <span className="pantry-review-row__name">{item.name}</span>
              <button
                type="button"
                className="pantry-review-row__edit-btn"
                onClick={() => onStartEdit(item.id)}
                title="Edit name"
                aria-label={`Edit ${item.name}`}
              >
                ✎
              </button>
            </div>
            {item.confidence === 'low' && item.suggestion ? (
              <div className="pantry-review-row__suggestion">
                Did you mean{' '}
                <button
                  type="button"
                  className="pantry-spelling-suggestions__choice"
                  onClick={() => onApplySuggestion(item.id, item.suggestion)}
                >
                  {item.suggestion}
                </button>
                ?
              </div>
            ) : null}
            {item.confidence === 'low' && !item.suggestion ? (
              <div className="pantry-review-row__unclear">Couldn&apos;t identify clearly — check before adding</div>
            ) : null}
          </div>
          {item.category ? <span className="pantry-review-row__category">{item.category}</span> : null}
          {item.quantity_display ? (
            <span className="pantry-review-row__qty" title="Not editable in this version">
              {item.quantity_display}
            </span>
          ) : null}
          <button
            type="button"
            className="pantry-review-row__discard"
            onClick={() => onDiscard(item.id)}
            title="Remove from list"
            aria-label={`Remove ${item.name} from review`}
          >
            ×
          </button>
        </>
      )}
    </div>
  )
}
